import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Exhibitions = () => {
  const [exhibitions, setExhibitions] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/exhibitions`).then(r => setExhibitions(r.data)),
      axios.get(`${API}/page-content/exhibitions`).then(r => setPageContent(r.data)).catch(() => {}),
    ]).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'All' ? exhibitions : exhibitions.filter(ex => ex.status === filter);

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="exhibitions-page">
      <SEO title="Exhibitions - Gangadhar Som" description="Past and upcoming exhibitions featuring artwork by Gangadhar Som."
        url={`${window.location.origin}/exhibitions`} />
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10 sm:mb-16">
          <span className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground">{pageContent?.page_label || 'Shows & Exhibitions'}</span>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-light tracking-tight mt-3 mb-5 sm:mb-8">{pageContent?.page_title || 'Exhibitions'}</h1>
          <p className="font-sans text-sm sm:text-base md:text-lg leading-relaxed text-foreground/80 max-w-3xl">
            {pageContent?.page_description || 'Explore past and upcoming exhibitions featuring the artwork of Gangadhar Som.'}
          </p>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-8 sm:mb-12">
          {['All', 'Upcoming', 'Current', 'Past'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-sans uppercase tracking-[0.15em] text-[10px] sm:text-xs font-medium transition-all ${
                filter === s ? 'bg-accent text-white shadow-md' : 'bg-card border border-border hover:border-accent'
              }`}>
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="skeleton h-40 rounded-lg" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Calendar size={44} className="mx-auto mb-3 text-muted-foreground" />
            <p className="font-sans text-muted-foreground">No exhibitions found.</p>
          </div>
        ) : (
          <div className="space-y-5 sm:space-y-8">
            {filtered.map((ex, i) => (
              <motion.div key={ex.id}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ delay: i * 0.08 }}
                className="bg-card rounded-lg overflow-hidden border border-border interactive-card"
              >
                <div className="flex flex-col sm:grid sm:grid-cols-3 gap-0">
                  {ex.image_url && (
                    <div className="sm:col-span-1">
                      <img src={ex.image_url} alt={ex.title} className="w-full h-48 sm:h-full object-cover" />
                    </div>
                  )}
                  <div className={`p-5 sm:p-6 md:p-8 ${ex.image_url ? 'sm:col-span-2' : 'sm:col-span-3'}`}>
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-sans uppercase tracking-wider mb-3 ${
                      ex.status === 'Current' ? 'bg-accent/10 text-accent' :
                      ex.status === 'Upcoming' ? 'bg-blue-500/10 text-blue-500' : 'bg-muted text-muted-foreground'
                    }`}>
                      {ex.status} · {ex.exhibition_type}
                    </span>
                    <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-light mb-3">{ex.title}</h2>
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin size={14} />
                        <span className="font-sans text-xs sm:text-sm">{ex.venue}, {ex.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar size={14} />
                        <span className="font-sans text-xs sm:text-sm">{ex.start_date}{ex.end_date && ` - ${ex.end_date}`}</span>
                      </div>
                    </div>
                    {ex.description && <p className="font-sans text-sm leading-relaxed text-foreground/80">{ex.description}</p>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Exhibitions;
